import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase credentials. Please click "Connect to Supabase" button to configure your project.');
}

// Custom fetch implementation with timeout and retry logic
const customFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const timeout = 30000; // 30 second timeout
  const maxRetries = 3;
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(input, {
        ...init,
        signal: controller.signal,
      });

      clearTimeout(id);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error: any) {
      lastError = error;
      console.error(`Attempt ${i + 1} failed:`, error);
      
      // Don't retry if we intentionally aborted or if it's a 4xx error
      if (error.name === 'AbortError' || (error instanceof Response && error.status >= 400 && error.status < 500)) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      if (i < maxRetries - 1) {
        const waitTime = Math.min(1000 * Math.pow(2, i), 10000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }

  throw lastError;
};

export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    global: {
      headers: {
        'X-Client-Info': 'hoito@1.0.0'
      },
      fetch: customFetch
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Add connection status check
export const checkConnection = async () => {
  try {
    const { error } = await supabase.auth.getSession();
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Supabase connection error:', error);
    return false;
  }
};

// Initialize connection check
checkConnection().then(isConnected => {
  if (!isConnected) {
    console.error('Unable to connect to Supabase. Please check your configuration.');
  } else {
    console.log('Successfully connected to Supabase');
  }
});